import { inject, Injectable } from '@angular/core';
import {
  type AddLearningResourceItemToSetInput,
  type AddLearningResourceItemToSetMutation,
  type AddLearningResourceItemToSetMutationVariables,
  type LearningResourceSetQuery,
  type LearningResourceSetQueryVariables,
  type LearningResourceSetWithGlobalCategoryQuery,
  type LearningResourceSetWithGlobalCategoryQueryVariables,
  type RemoveLearningResourceItemFromSetInput,
  type RemoveLearningResourceItemFromSetMutation,
  type RemoveLearningResourceItemFromSetMutationVariables,
  type UpdateLearningResourceSetInput,
  type UpdateLearningResourceSetItemInput,
  type UpdateLearningResourceSetItemMutation,
  type UpdateLearningResourceSetItemMutationVariables,
  type UpdateLearningResourceSetMutationMutation,
  type UpdateLearningResourceSetMutationMutationVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { orderBy } from 'lodash-es';
import { map } from 'rxjs/operators';
import { type BatchMutationVariables } from '../../../services/graphql.service';
import { SharedGraphQlService } from '../../../services/shared-graphql.service';
import { assertGraphqlDataNode } from '../../../utils/type-helpers';
import { batchUpdateLearningResourceSetItem } from './../learning-resource-set-item.batch-mutation';
import { learningResourceSetWithItemsFragment } from './../learning-resource-set-with-items.fragment';
import { learningResourceSetMinimal } from './../learning-resource-set.minimal';
import { batchUpdateLearningResourceSet } from './../learning-resource.batch-mutation';

export const learningResourceSetQuery = gql`
  query LearningResourceSet($learningResourceSetId: ID!) {
    node(id: $learningResourceSetId) {
      ... on LearningResourceSet {
        ...LearningResourceSetWithItems
      }
    }
  }
  ${learningResourceSetWithItemsFragment}
`;

export const learningResourceSetWithGlobalCategoryQuery = gql`
  query LearningResourceSetWithGlobalCategory($learningResourceSetId: ID!) {
    node(id: $learningResourceSetId) {
      ... on LearningResourceSet {
        ...LearningResourceSetWithItems
        learningResourceGlobalCategory {
          id
          title
        }
      }
    }
  }
  ${learningResourceSetWithItemsFragment}
`;

@Injectable({ providedIn: 'root' })
export class SharedLearningResourceApi {
  private readonly sharedGraphQlService = inject(SharedGraphQlService);

  watchLearningResourceSet(learningResourceSetId: string) {
    return this.sharedGraphQlService
      .watchQuery<LearningResourceSetQuery, LearningResourceSetQueryVariables>({
        query: learningResourceSetQuery,
        variables: { learningResourceSetId },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'LearningResourceSet')));
  }

  watchLearningResourceSetItems(learningResourceSetId: string) {
    return this.watchLearningResourceSet(learningResourceSetId).pipe(
      // @ts-expect-error TS2339 property does not exist
      map((set) => orderBy(set.learningResourceItems.edges, (it) => it.order)),
    );
  }

  watchLearningResourceSetWithGlobalCategory(learningResourceSetId: string) {
    return this.sharedGraphQlService
      .watchQuery<LearningResourceSetWithGlobalCategoryQuery, LearningResourceSetWithGlobalCategoryQueryVariables>({
        query: learningResourceSetWithGlobalCategoryQuery,
        variables: { learningResourceSetId },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'LearningResourceSet')));
  }

  updateLearningSetOrder(learningResourceSets: { id: string; order: number }[]) {
    const toUpdate = learningResourceSets.map((lr, index) => ({ learningResourceSetId: lr.id, order: index }));
    const mutations: string[] = [];
    const variables: { [variableKey: string]: BatchMutationVariables }[] = [];

    toUpdate.forEach((learningResourceSetInput: UpdateLearningResourceSetInput) => {
      mutations.push(batchUpdateLearningResourceSet);
      variables.push({
        learningResourceSetInput: {
          type: 'UpdateLearningResourceSetInput!',
          value: learningResourceSetInput,
        },
      });
    });

    return this.sharedGraphQlService.mutateBatch({
      mutations,
      variables,
    });
  }

  removeItemFromLearningSet(input: RemoveLearningResourceItemFromSetInput) {
    return this.sharedGraphQlService.mutateV2<
      RemoveLearningResourceItemFromSetMutation,
      RemoveLearningResourceItemFromSetMutationVariables
    >({
      mutation: gql`
        mutation removeLearningResourceItemFromSet($input: RemoveLearningResourceItemFromSetInput!) {
          removeLearningResourceItemFromSet(input: $input) {
            errors {
              message
              path
            }
          }
        }
      `,
      variables: { input },
      refetchQueries: [
        {
          query: learningResourceSetQuery,
          variables: { learningResourceSetId: input.learningResourceSetId },
        },
      ],
    });
  }

  addItemToLearningSet(input: AddLearningResourceItemToSetInput) {
    return this.sharedGraphQlService.mutateV2<
      AddLearningResourceItemToSetMutation,
      AddLearningResourceItemToSetMutationVariables
    >({
      mutation: gql`
        mutation addLearningResourceItemToSet($input: AddLearningResourceItemToSetInput!) {
          addLearningResourceItemToSet(input: $input) {
            errors {
              message
              path
            }
          }
        }
      `,
      variables: { input },
      refetchQueries: [
        {
          query: learningResourceSetQuery,
          variables: { learningResourceSetId: input.learningResourceSetId },
        },
      ],
    });
  }

  updateLearningSetItem(input: UpdateLearningResourceSetItemInput) {
    return this.sharedGraphQlService.mutateV2<
      UpdateLearningResourceSetItemMutation,
      UpdateLearningResourceSetItemMutationVariables
    >({
      mutation: gql`
        mutation updateLearningResourceSetItem($input: UpdateLearningResourceSetItemInput!) {
          updateLearningResourceSetItem(input: $input) {
            errors {
              message
              path
            }
          }
        }
      `,
      variables: { input },
      refetchQueries: [
        {
          query: learningResourceSetQuery,
          variables: { learningResourceSetId: input.learningResourceSetId },
        },
      ],
    });
  }

  updateLearningSetItemsOrder(learningResourceSetId: string, learningResourceSetItems: { id: string }[]) {
    const toUpdate: UpdateLearningResourceSetItemInput[] = learningResourceSetItems.map((lr, index) => ({
      learningResourceItemId: lr.id,
      learningResourceSetId,
      order: index,
    }));
    const mutations: string[] = [];
    const variables: { [variableKey: string]: BatchMutationVariables }[] = [];

    toUpdate.forEach((learningResourceSetInput) => {
      mutations.push(batchUpdateLearningResourceSetItem);
      variables.push({
        learningResourceSetItemInput: {
          type: 'UpdateLearningResourceSetItemInput!',
          value: learningResourceSetInput,
        },
      });
    });

    return this.sharedGraphQlService.mutateBatch({
      mutations,
      variables,
      refetchQueries: [
        {
          query: learningResourceSetQuery,
          variables: { learningResourceSetId },
        },
      ],
    });
  }

  updateLearningResourceSet(learningResourceSet: UpdateLearningResourceSetInput) {
    return (
      this.sharedGraphQlService
        .mutateV2<UpdateLearningResourceSetMutationMutation, UpdateLearningResourceSetMutationMutationVariables>({
          mutation: gql`
            mutation UpdateLearningResourceSetMutation($input: UpdateLearningResourceSetInput!) {
              updateLearningResourceSet(input: $input) {
                errors {
                  message
                  path
                }
                learningResourceSet {
                  ...LearningResourceSetMinimal
                }
              }
            }
            ${learningResourceSetMinimal}
          `,
          variables: {
            input: learningResourceSet,
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.updateLearningResourceSet.learningResourceSet))
    );
  }
}
